import { useMemo } from 'react'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchReportsQuery } from '@/api/workspace-api/report-api'
import { usePermissions } from '@/hooks/use-permissions'
import {
  BasicService,
  ReportService,
  ServiceCategory,
} from '@/types/mock-api-types'

interface Limit {
  numberOfReports: number
  numberOfKols: number
}

const UNLIMITED = Number.MAX_VALUE

type UseReportLimit = () => {
  limited: boolean
  limit: Limit
  isReportLimitReached: boolean
}

const useReportLimit: UseReportLimit = () => {
  const { data: userStatus } = useFetchUserStatusQuery()
  const { data } = useFetchReportsQuery({ page: 1 })
  const { withInternalPermission } = usePermissions()

  const isReportService = (service?: BasicService): service is ReportService =>
    service?.serviceCategory === ServiceCategory.Report

  const foundReportService = (): ReportService | undefined =>
    userStatus?.currentWorkspace.availableServices.find(isReportService)

  const reportService = foundReportService()
  const limit: Limit = useMemo(() => {
    if (withInternalPermission) {
      return {
        numberOfReports: UNLIMITED,
        numberOfKols: UNLIMITED,
      }
    }

    if (reportService !== undefined) {
      return {
        numberOfReports: reportService.memo.reportReportLimit,
        numberOfKols: reportService.memo.reportKolLimit,
      }
    }

    return {
      numberOfReports: 0,
      numberOfKols: 0,
    }
  }, [withInternalPermission, reportService])

  const limited = limit.numberOfReports !== UNLIMITED

  const isReportLimitReached = useMemo<boolean>(() => {
    if (limited) {
      return (data?.total ?? 0) >= limit.numberOfReports
    }

    return false
  }, [limited, data, limit])

  return {
    limited,
    limit,
    isReportLimitReached,
  }
}

export default useReportLimit
